import { type EditorItem } from '#app/utils/workout-divisions'

export type WorkoutDraftSection = {
	stringInput: string
	sortOrder: number
	division: string | null
}

const keyFor = (dateKey: string) => `workout-draft:new:${dateKey}`

export function readWorkoutDraft(
	dateKey: string,
): WorkoutDraftSection[] | null {
	if (typeof window === 'undefined') return null
	try {
		const raw = window.localStorage.getItem(keyFor(dateKey))
		if (!raw) return null
		const parsed = JSON.parse(raw) as WorkoutDraftSection[]
		return Array.isArray(parsed) && parsed.length > 0 ? parsed : null
	} catch {
		return null
	}
}

export function writeWorkoutDraft(
	dateKey: string,
	sections: WorkoutDraftSection[],
) {
	if (typeof window === 'undefined') return
	try {
		// Don't persist an empty/blank draft.
		const meaningful = sections.filter((s) => s.stringInput.trim().length > 0)
		if (meaningful.length === 0) {
			window.localStorage.removeItem(keyFor(dateKey))
			return
		}
		window.localStorage.setItem(keyFor(dateKey), JSON.stringify(sections))
	} catch {}
}

export function clearWorkoutDraft(dateKey: string) {
	if (typeof window === 'undefined') return
	try {
		window.localStorage.removeItem(keyFor(dateKey))
	} catch {}
}

/**
 * Derive a client-safe `WorkoutDraftSection[]` from an `EditorItem[]` list.
 * Each section is tagged with the most recent preceding marker's division
 * (null before any marker, or after a close marker), and assigned a sequential
 * sortOrder. Markers themselves are dropped — only sections are persisted; the
 * restore path rebuilds markers from the section divisions.
 *
 * This mirrors the section/division derivation the server performs when it
 * splits a submitted workout document (app/utils/update-workout.tsx) but stays
 * free of server-only imports so it can run in the browser.
 */
export function draftSectionsFromItems(
	items: EditorItem[],
): WorkoutDraftSection[] {
	const sections: WorkoutDraftSection[] = []
	let currentDivision: string | null = null
	let sortOrder = 0
	for (const item of items) {
		if (item.kind === 'marker') {
			currentDivision = item.division
			continue
		}
		sections.push({
			stringInput: item.stringInput.replace(/\r/g, ''),
			sortOrder,
			division: currentDivision,
		})
		sortOrder++
	}
	return sections
}
